import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Save, Eye, RotateCcw, Check, AlertCircle } from 'lucide-react';
import ContentEditor from '../../components/admin/ContentEditor';
import { api } from '../../lib/api';
import { useContent } from '../../lib/content';

const TITLES = {
  site: 'Sitio · Branding',
  seo: 'SEO',
  intro_hero: 'Intro / Hero',
  manifesto: 'Manifiesto',
  showcase: 'Showcase / Antes-Después',
  features: 'Plataforma · Pilares de control',
  how: 'Pipeline de producción',
  comparison: 'vs Herramientas genéricas',
  looks_hall_of_fame: 'Looks · Hall of Fame',
  capabilities: 'Capacidades IA',
  pricing: 'Precios',
  testimonials: 'Testimonios',
  logos: 'Logos clientes',
  team: 'Equipo',
  faq: 'FAQ',
  contact: 'Contacto',
  footer: 'Footer',
};

export default function ContentSectionEditor() {
  const { section } = useParams();
  const [data, setData] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('idle');
  const { refresh } = useContent();

  useEffect(() => {
    setLoading(true);
    setStatus('idle');
    api.get(`/admin/content/${section}`)
      .then(({ data }) => {
        setData(data?.data || {});
        setOriginal(data?.data || {});
      })
      .finally(() => setLoading(false));
  }, [section]);

  const dirty = JSON.stringify(data) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true);
    setStatus('idle');
    try {
      await api.put(`/admin/content/${section}`, { data });
      setOriginal(data);
      await refresh();
      setStatus('ok');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('err');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`content-page-${section}`}>
      <div className="flex items-center justify-between mb-8 pb-6 border-b border-line gap-4 flex-wrap">
        <div>
          <p className="text-eyebrow text-ink-muted mb-2">CMS · {section}</p>
          <h1 className="font-display text-4xl md:text-5xl text-ink">{TITLES[section] || section}</h1>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`/#${section}`}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2.5 border border-line-strong rounded-full text-xs font-mono uppercase tracking-[0.18em] text-ink-muted hover:text-acid hover:border-acid flex items-center gap-2"
            data-testid="preview-section"
          >
            <Eye size={12} /> Ver en sitio
          </a>
          {dirty && (
            <button
              onClick={() => setData(original)}
              className="px-4 py-2.5 border border-line-strong rounded-full text-xs font-mono uppercase tracking-[0.18em] text-ink-muted hover:text-ink"
              data-testid="reset-changes"
            >
              <RotateCcw size={12} className="inline mr-1.5" /> Descartar
            </button>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-acid !px-6 !py-2.5 !text-[11px] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-section"
          >
            <Save size={13} /> {saving ? 'Guardando…' : (dirty ? 'Publicar cambios' : 'Sin cambios')}
          </button>
        </div>
      </div>

      {status === 'ok' && (
        <div className="mb-6 px-4 py-3 bg-acid/10 border border-acid/40 text-acid rounded-md text-sm font-mono flex items-center gap-2" data-testid="save-success">
          <Check size={14} /> Cambios publicados.
        </div>
      )}
      {status === 'err' && (
        <div className="mb-6 px-4 py-3 bg-danger/10 border border-danger/40 text-danger rounded-md text-sm font-mono flex items-center gap-2" data-testid="save-error">
          <AlertCircle size={14} /> No se pudo guardar.
        </div>
      )}

      {loading
        ? <div className="text-ink-muted py-20 text-center">Cargando…</div>
        : <ContentEditor data={data} onChange={setData} />
      }
    </div>
  );
}
